import { useReduxState } from 'hooks';
import { Button } from 'components/common';
import { Box } from 'components/common/box';
import {
  ArrayField,
  DisplayField,
  FormGroup,
  LanguageVariants,
  Text
} from 'components/common/form';

import type { UserCustomAttribute } from 'shared-types';

interface Props {
  initialRequiredPasswords?: UserCustomAttribute['validation']['requiredPasswords'];
}

const UserAttributesRequirePassword = ({ initialRequiredPasswords }: Props) => {
  const { messages, theme } = useReduxState();

  return (
    <Box
      title={messages.authRequirePassword}
      titleTooltip="Note: password is currently required only for non-admins"
    >
      <div className="content">
        <ArrayField field="validation.requiredPasswords">
          {({ add }) => (
            <>
              <div className="items">
                <ArrayField.Items>
                  {({ remove, field, index, key }) => {
                    return (
                      <div className="item">
                        <div className="item-content">
                          <div className="item-content-inner">
                            <FormGroup label={messages.commonTitle}>
                              <Text
                                field={`${field}.title`}
                                placeholder={messages.commonAddText}
                              />

                              <LanguageVariants
                                field={`${field}.titleTranslations`}
                                type="text"
                              />
                            </FormGroup>

                            <FormGroup label={messages.commonPassword}>
                              <Text
                                field={`${field}.passwordValue`}
                                placeholder={messages.authEnterPassword}
                              />
                            </FormGroup>

                            <FormGroup
                              label="Value match regex"
                              labelTooltip="If specified, password will be enforced only if value matches this regex, default is to always enforce password"
                            >
                              <Text
                                field={`${field}.valueMatchRegex`}
                                placeholder="Add optional regex"
                              />
                            </FormGroup>

                            <DisplayField
                              field={`${field}.passwordIdentifier`}
                              initialValue={
                                initialRequiredPasswords?.[index]
                                  ?.passwordIdentifier ||
                                `user-custom-attribute-password-${key}`
                              }
                            />
                          </div>
                        </div>

                        <div className="item-action">
                          <Button
                            aria-label={messages.commonDelete}
                            category="light"
                            data-tooltip={messages.commonDelete}
                            icon="trash-alt"
                            onClick={remove}
                            size="small"
                          />
                        </div>
                      </div>
                    );
                  }}
                </ArrayField.Items>
              </div>

              <Button
                category="light"
                icon="plus"
                onClick={add}
                size="small"
                style={{ marginTop: 10 }}
                text="Add password"
              />
            </>
          )}
        </ArrayField>

        <style jsx>{`
          .header {
            display: flex;
            gap: 10px;
            margin-bottom: 5px;
            padding: 0 2px;
          }
          .items {
            align-items: flex-start;
            display: flex;
            flex-direction: column;
            gap: 15px;
            width: 100%;
          }
          .item {
            border: 1px solid ${theme.lightGreyDark};
            border-radius: ${theme.cardBorderRadius};
            padding: 10px;
            display: flex;
            gap: 20px;
            width: 100%;
          }
          .item-content {
            flex-grow: 1;
          }
          .item-content-inner {
            max-width: 360px;
          }
          .password {
            width: 220px;
          }
          .value-match {
            width: 220px;
          }
        `}</style>
      </div>
    </Box>
  );
};

export default UserAttributesRequirePassword;
